import { collection, onSnapshot, orderBy, query, type Timestamp } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * 現セッション選択 + sessions subscribe (user 第 2 弾、Phase 5、admin 第 16+17 弾の後追い)。
 *
 * 役割:
 *   - 地区配下の active session 一覧を subscribe (archivedAt=null のもののみ表示)
 *   - 現セッション (currentSessionId) を localStorage `cw.currentSessionId:{districtId}` に永続化
 *   - Announcement.targetSessionIds filter / Post.sessionId 自動付与の source
 *
 * 旧 v1 では Onboarding で `sessionStorage[pendingSessionId:{token}]` に保持していたが、
 * v2 minimum では Onboarding フローを Phase 5 後続で実装するまでの繋ぎとして本 hook で代用。
 * Onboarding 完成時には initial value を URL 経由で受け取る統合に拡張する。
 */

export type CurrentSessionLevel = 'normal'; // 将来 'master'/'guest' 等の拡張余地

export interface SessionItem {
  id: string;
  name: string;
  code: string;
  startAt: Date | null;
  endAt: Date | null;
  /** 表示順 (admin がセッション管理で設定、小さいほど先頭、未設定は startAt 降順フォールバック)。 */
  sortOrder?: number;
}

/**
 * セッション表示順の pure ソート (admin sessions-admin/useSessions.ts の sortSessions と同一仕様)。
 *
 * 仕様: `sortOrder` 昇順 (小さいほど先頭、未設定は末尾) → 同値 / 双方未設定は startAt 降順
 * (新しいものが先頭、startAt=null は最後)。apps/* 間の直接 import は ESLint boundaries で禁止のため
 * admin 側と共通化できず、同等関数を user 側にも複製している (仕様は 1 箇所で記述)。元配列は破壊しない。
 */
export function sortSessions<T extends { sortOrder?: number; startAt: Date | null }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const ao = typeof a.sortOrder === 'number' ? a.sortOrder : Number.POSITIVE_INFINITY;
    const bo = typeof b.sortOrder === 'number' ? b.sortOrder : Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    const at = a.startAt ? a.startAt.getTime() : Number.NEGATIVE_INFINITY;
    const bt = b.startAt ? b.startAt.getTime() : Number.NEGATIVE_INFINITY;
    return bt - at;
  });
}

export type CurrentSessionState =
  | { status: 'loading'; items: []; currentSessionId: string | null; error: null }
  | { status: 'ready'; items: SessionItem[]; currentSessionId: string | null; error: null }
  | { status: 'error'; items: []; currentSessionId: string | null; error: Error };

function storageKey(districtId: string): string {
  return `cw.currentSessionId:${districtId}`;
}

function loadStored(districtId: string | null): string | null {
  if (!districtId) return null;
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(storageKey(districtId));
  } catch {
    return null;
  }
}

function persistStored(districtId: string | null, value: string | null): void {
  if (!districtId) return;
  if (typeof localStorage === 'undefined') return;
  try {
    if (value === null) localStorage.removeItem(storageKey(districtId));
    else localStorage.setItem(storageKey(districtId), value);
  } catch {
    /* quota / private mode 等は無視 */
  }
}

function tsToDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as Timestamp).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

export function useCurrentSession(districtId: string | null): {
  state: CurrentSessionState;
  setCurrentSessionId: (id: string | null) => void;
} {
  const [state, setState] = useState<CurrentSessionState>({
    status: 'loading',
    items: [],
    currentSessionId: loadStored(districtId),
    error: null,
  });

  useEffect(() => {
    // districtId 切替時は localStorage からの初期値再読込
    setState((prev) => ({
      ...prev,
      status: 'loading',
      items: [],
      currentSessionId: loadStored(districtId),
      error: null,
    }));

    // 地区フォールバック根絶: districtId 未解決 (null/空) のときは phase2-default の
    // sessions を購読しない (現セッション誤確定を防ぐ)。
    if (!districtId) return;

    // closure に districtId を capture (Firebase onSnapshot は cleanup 後にも 1 件遅延 callback が届く
    // ケースがあり、新 districtId の localStorage を旧 callback が誤って書き換えるのを防ぐため)。
    const capturedDistrictId = districtId;
    let cancelled = false;

    const q = query(
      collection(db, 'districts', districtId, 'sessions'),
      orderBy('startAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (cancelled) return;
        const items: SessionItem[] = sortSessions(
          snap.docs
            .filter((d) => {
              const data = d.data();
              return !tsToDate(data.archivedAt);
            })
            .map((d) => {
              const data = d.data();
              return {
                id: d.id,
                name: typeof data.name === 'string' ? data.name : '',
                code: typeof data.code === 'string' ? data.code : '',
                startAt: tsToDate(data.startAt),
                endAt: tsToDate(data.endAt),
                sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : undefined,
              };
            }),
        );
        setState((prev) => {
          // 選択中 session が archive / 削除された場合は currentSessionId を null に reset
          const stillExists =
            prev.currentSessionId && items.some((s) => s.id === prev.currentSessionId);
          const nextCurrent = stillExists ? prev.currentSessionId : null;
          if (!stillExists && prev.currentSessionId !== null) {
            persistStored(capturedDistrictId, null);
          }
          return {
            status: 'ready',
            items,
            currentSessionId: nextCurrent,
            error: null,
          };
        });
      },
      (error) => {
        if (cancelled) return;
        setState((prev) => ({
          status: 'error',
          items: [],
          currentSessionId: prev.currentSessionId,
          error,
        }));
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [districtId]);

  function setCurrentSessionId(id: string | null): void {
    persistStored(districtId, id);
    setState((prev) => ({ ...prev, currentSessionId: id }));
  }

  return { state, setCurrentSessionId };
}
